import React, { useEffect, useMemo, useState } from 'react';
import {
  ShieldCheck, Gift, ArrowRight, Lock, Server, Scale, HardDrive,
  Store, Truck, Network, Users, Grid, Wrench, PieChart, Headphones, FileText, Zap, Heart,
} from 'lucide-react';
import LogoImage from '../../components/Common/LogoImage';
import { appSidebarLogoSources, AUTH_HERO_VILLAGE_WEBP, AUTH_HERO_VILLAGE_JPG } from '../../utils/platformAssets';
import seoService from '../../services/seo.service';
import { getPublicDomainUrl, getAppDomainUrl } from '../../utils/constants';
import { PUBLIC_SITE_URL, WAYGPT_URL, getPublicSiteHostname } from '../../utils/externalLinks';
import PlatformModuleIcon from '../../components/Common/PlatformModuleIcon';
import { BetaBadge, VersionBadge } from '../../components/Common/VersionBadge';
import { useAppInfo } from '../../hooks/useAppInfo';

const currentYear = new Date().getFullYear();

const platformModules = [
  { assetKey: 'Shop', icon: <Store size={18} />, title: 'Интернет-магазин', desc: 'Витрина и оплата' },
  { assetKey: 'Market', icon: <Grid size={18} />, title: 'Маркетплейсы', desc: 'Делграм и площадки' },
  { assetKey: 'Delivery', icon: <Truck size={18} />, title: 'Склад и доставка', desc: '8 служб доставки' },
  { assetKey: 'Assistant', icon: <Headphones size={18} />, title: 'AI-помощник', desc: 'Ответы 24/7' },
  { assetKey: 'Orders', icon: <FileText size={18} />, title: 'Документы', desc: 'ЭДО и спецификации' },
  { assetKey: 'CRM', icon: <Users size={18} />, title: 'Клиенты и HR', desc: 'CRM и команда' },
  { assetKey: 'Finance', icon: <PieChart size={18} />, title: 'Финансы', desc: 'Отчёты и касса' },
  { assetKey: 'WMS', icon: <Wrench size={18} />, title: 'Производство', desc: 'Сборка и себестоимость' },
];

const trustItems = [
  { icon: <Lock size={16} />, label: 'Шифрование TLS 1.3' },
  { icon: <HardDrive size={16} />, label: 'Резервное копирование' },
  { icon: <Server size={16} />, label: 'Серверы в России' },
  { icon: <Scale size={16} />, label: 'Соответствие 152-ФЗ' },
];

const AuthLayout = ({ children }) => {
  const publicUrl = (path) => getPublicDomainUrl(path);
  const [siteLogoUrl, setSiteLogoUrl] = useState(null);
  const { version: appVersion } = useAppInfo();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await seoService.getMetaTags('landing');
        if (!mounted || !res?.success) return;
        const url = res.data?.branding?.site_logo_url;
        if (url && (url.startsWith('/') || /^https?:\/\//i.test(url))) {
          setSiteLogoUrl(url);
        }
      } catch (_) { /* ignore */ }
    })();
    return () => { mounted = false; };
  }, []);

  const authLogoSources = useMemo(
    () => appSidebarLogoSources(null, siteLogoUrl),
    [siteLogoUrl]
  );

  return (
    <div className="auth-container mb-auth-global-wrap auth-container--start">
      <div className="auth-page-bg" aria-hidden>
        <picture>
          <source srcSet={AUTH_HERO_VILLAGE_WEBP} type="image/webp" />
          <img
            src={AUTH_HERO_VILLAGE_JPG}
            alt=""
            className="auth-page-bg__img"
            width={1600}
            height={900}
            loading="eager"
            decoding="async"
          />
        </picture>
        <div className="auth-page-bg__scrim" />
      </div>
      <div className="auth-orb auth-orb--1" aria-hidden />
      <div className="auth-orb auth-orb--2" aria-hidden />
      <div className="auth-orb auth-orb--3" aria-hidden />

      <header className="auth-topbar">
        <div className="auth-brand">
          <div className="auth-logo auth-logo-container">
            <LogoImage
              sources={authLogoSources}
              alt="Маркбэйс"
              mode="image"
              height={36}
              maxWidth={120}
              style={{ marginRight: 0 }}
            />
          </div>
          <div className="auth-brand-text">
            <div className="auth-brand-row">
              <h1 className="auth-brand-name">Маркбэйс</h1>
              <span className="auth-brand-version" title={`Версия ${appVersion}`}>v{appVersion}</span>
              <BetaBadge />
            </div>
            <span className="auth-brand-sub">Платформа для бизнеса</span>
          </div>
        </div>
        <div className="auth-topbar-secure">
          <ShieldCheck size={16} aria-hidden />
          <span>Безопасный вход</span>
        </div>
      </header>

      <div className="auth-main">
        <section className="auth-sidebar" aria-label="О платформе">
          <div className="auth-content">
            <div className="auth-headline">
              <p className="auth-eyebrow">Единая платформа для вашего бизнеса</p>
              <h2 className="auth-title">
                Всё для торговли<br />
                <span className="auth-title-grad">в одном месте</span>
              </h2>
              <p className="auth-description">
                Магазин, склад, доставка, документы и аналитика без комиссии с каждой продажи.
                Подключайте модули по мере роста — один аккаунт на всю экосистему.
              </p>
            </div>

            <div className="auth-modules-showcase">
              <div className="auth-modules-label">Модули платформы</div>
              <div className="auth-modules-grid auth-modules-grid--compact">
                {platformModules.map((mod, i) => (
                  <div
                    className="auth-module-card"
                    key={mod.assetKey}
                    style={{ animationDelay: `${i * 0.05}s` }}
                  >
                    <div className="auth-module-icon">
                      <PlatformModuleIcon
                        assetKey={mod.assetKey}
                        fallback={mod.icon}
                        forceDark
                        className="auth-module-icon-inner"
                        imgClassName="auth-module-icon-img"
                      />
                    </div>
                    <div className="auth-module-info">
                      <div className="auth-module-title">{mod.title}</div>
                      <div className="auth-module-desc">{mod.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="auth-trust-row">
              {trustItems.map((item) => (
                <div className="auth-trust-item" key={item.label}>
                  <span className="auth-trust-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>

            <div className="auth-cta-banner">
              <div className="auth-cta-banner__text">
                <Gift size={18} aria-hidden />
                <div>
                  <strong>Новый здесь?</strong>
                  <span>Краткий обзор платформы и тарифов</span>
                </div>
              </div>
              <a href={publicUrl('/about')} className="auth-cta-banner__btn">
                Узнать больше
                <ArrowRight size={16} aria-hidden />
              </a>
            </div>
          </div>
        </section>

        <section className="auth-form-container" aria-label="Вход">
          <div className="auth-form-shell">
            <div className="auth-form-content">
              {children}
            </div>
          </div>
        </section>
      </div>

      <footer className="auth-footer-full">
        <div className="auth-footer-links">
          <a href={publicUrl('/about')} className="auth-footer-link">О платформе</a>
          <span className="auth-footer-dot" />
          <a href={publicUrl('/pricing')} className="auth-footer-link">Тарифы</a>
          <span className="auth-footer-dot" />
          <a href={publicUrl('/product/core')} className="auth-footer-link">Модули</a>
          <span className="auth-footer-dot" />
          <a href={publicUrl('/docs')} className="auth-footer-link">Документация</a>
          <span className="auth-footer-dot" />
          <a href={publicUrl('/contact')} className="auth-footer-link">Поддержка</a>
        </div>
        <div className="auth-footer-links auth-footer-links--legal">
          <a href={publicUrl('/terms')} className="auth-footer-link">Условия использования</a>
          <span className="auth-footer-dot" />
          <a href={publicUrl('/privacy')} className="auth-footer-link">Политика конфиденциальности</a>
          <span className="auth-footer-dot" />
          <a href={publicUrl('/personal-data')} className="auth-footer-link">Персональные данные</a>
        </div>
        <div className="auth-footer-divider" />
        <div className="auth-footer-ecosystem">
          <div className="auth-footer-modules">
            <span className="auth-footer-eco-label">Экосистема</span>
            <div className="auth-footer-module-badges">
              <a href={getAppDomainUrl('/')} className="auth-footer-badge" title="Панель управления">
                <Zap size={14} aria-hidden /> Панель
              </a>
              <a href="https://delgram.ru" className="auth-footer-badge" title="Площадка товаров">
                <Store size={14} aria-hidden /> Делграм
              </a>
              <a href={WAYGPT_URL} className="auth-footer-badge" title="Умный помощник">
                <Network size={14} aria-hidden /> Помощник
              </a>
            </div>
          </div>
        </div>
        <div className="auth-footer-bottom">
          <div className="auth-footer-copyright">
            <a href={PUBLIC_SITE_URL} className="auth-footer-brand-link" target="_blank" rel="noopener noreferrer">
              <img src="/brand/ecosystem/markbase-mark.svg" alt="" width={20} height={20} /> {getPublicSiteHostname()}
            </a>
            <span className="auth-footer-copy-text">
              &copy; {currentYear} Markbase. Платформа для бизнеса.
            </span>
          </div>
          <div className="auth-footer-dev">
            <VersionBadge showCountdown={false} />
            <a
              href="https://w7c.ru"
              className="auth-footer-studio"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/brand/ecosystem/w7c-mark.svg" alt="" width={20} height={20} />
              w7c.ru
            </a>
            <span className="auth-footer-heart">
              Сделано с <Heart size={10} aria-hidden /> в России
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AuthLayout;
